import React from "react";
import styles from "./ai-rag-chat-modal.module.scss";
import { Button } from "@src/components/atoms/buttons/button/button";
import {
  ImagePickerButton,
  ImagePickerPreviews,
  useImagePaste,
} from "@src/components/atoms/image-picker/image-picker";
import { useI18N } from "@src/language/language-context";

const cssClassName = "ai-rag-chat-modal";

interface ChatInputProps {
  value: string;
  onChange: (value: string) => void;
  onSend: () => void;
  isLoading: boolean;
  inputRef?: React.RefObject<HTMLInputElement>;
  selectedFiles?: File[];
  onAddFiles?: (files: File[]) => void;
  onRemoveFile?: (index: number) => void;
}

export const ChatInput: React.FC<ChatInputProps> = ({
  value,
  onChange,
  onSend,
  isLoading,
  inputRef,
  selectedFiles = [],
  onAddFiles,
  onRemoveFile,
}) => {
  const i18n = useI18N();

  const texts = {
    imagePickerLabel: i18n("modules.aiRagChat.imagePickerLabel"),
    imageRemoveAlt: i18n("modules.aiRagChat.imageRemoveAlt"),
    imageTooMany: i18n("modules.aiRagChat.imageTooMany"),
    imageSizeTooLarge: i18n("modules.aiRagChat.imageSizeTooLarge"),
    imageInvalidType: i18n("modules.aiRagChat.imageInvalidType"),
  };

  const imageErrorMessages = {
    tooMany: texts.imageTooMany,
    tooLarge: texts.imageSizeTooLarge,
    invalid: texts.imageInvalidType,
  };
  const handleImagePaste = useImagePaste(
    selectedFiles,
    (files) => onAddFiles?.(files),
    imageErrorMessages,
    isLoading,
  );

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      onSend();
    }
  };

  return (
    <div className={styles[`${cssClassName}__input-container`]}>
      <div className={styles[`${cssClassName}__input-row`]}>
        <input
          ref={inputRef}
          className={styles[`${cssClassName}__input`]}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyPress={handleKeyPress}
          onPaste={handleImagePaste}
          placeholder="Ask a question..."
          disabled={isLoading}
        />
        <ImagePickerButton
          images={selectedFiles}
          onAdd={(files) => onAddFiles?.(files)}
          disabled={isLoading}
          pickerLabel={texts.imagePickerLabel}
          errorMessages={imageErrorMessages}
        />
        <Button
          fun={onSend}
          text={isLoading ? "..." : "Send"}
          variant="primary"
          disabled={isLoading || !value.trim()}
        />
      </div>
      <ImagePickerPreviews
        images={selectedFiles}
        onRemove={(index) => onRemoveFile?.(index)}
        removeLabel={texts.imageRemoveAlt}
        disabled={isLoading}
      />
    </div>
  );
};
